import { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { EditorState, type Plugin } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet, EditorView } from '@atlaskit/editor-prosemirror/view';

import { createInEditorUserContentDecorationPlugin } from './in-editor-pm-plugins/in-editor-user-content-decoration/in-editor-user-content-decoration-plugin';
import { inEditorUserContentDecorationPluginKey } from './in-editor-pm-plugins/in-editor-user-content-decoration/in-editor-user-content-decoration-plugin-key';
import { createCommand } from './in-editor-streaming-plugin-factory';
import { expandPositionsToDocDepth } from './utils/expand-positions';

const createHiddenNodeDecoration = (from: number, to: number) => {
	return Decoration.node(
		from,
		to,
		{
			class: 'ai-inline-streaming-hidden',
			'data-testid': 'ai-inline-streaming-hidden',
		},
		{ key: 'inlineStreamingDecoration' },
	);
};

export const createAIResponseDecoration = (
	[startPos, endPos]: [number, number],
	state: EditorState,
) => {
	const widgetElement = document.createElement('span');
	widgetElement.setAttribute('data-testid', `ai-in-editor-widget`);
	widgetElement.className = `ai-in-editor-widget`;

	const [start, end] = expandPositionsToDocDepth({
		doc: state.doc,
		startPos,
		endPos,
	});

	const offset = startPos - start;
	const originalContent = state.doc.slice(start, end);

	const schema = new Schema({
		nodes: state.schema.spec.nodes,
		marks: state.schema.spec.marks,
	});

	const originalCutContent = PMNode.fromJSON(schema, state.doc.cut(start, end).toJSON());
	/**
	 * We want to inherit any node views from the parent document and retain all the functionality
	 */

	const plugins = [
		...state.plugins,
		createInEditorUserContentDecorationPlugin([offset, offset + endPos - startPos]),
	].reduce((acc, plugin) => {
		if (plugin.props.nodeViews) {
			acc.push({
				...plugin,
				getState: plugin.getState,
				props: {
					nodeViews: plugin.props.nodeViews,
				},
			});
		} else {
			acc.push({
				...plugin,
				props: {},
				spec: {
					...plugin.spec,
					view: () => ({}),
					filterTransaction: () => true,
					appendTransaction: () => undefined,
				},
				getState: plugin.getState,
			});
		}
		return acc;
	}, [] as Plugin[]);

	const innerEditorState = EditorState.create({
		doc: originalCutContent || undefined,
		schema,
	}).reconfigure({ plugins });

	const innerEditorView = new EditorView(widgetElement, {
		// You can use any node as an editor document
		state: innerEditorState,
		decorations: (state) => {
			const pluginState = inEditorUserContentDecorationPluginKey.getState(state);
			if (!pluginState) {
				return DecorationSet.empty;
			}
			const decorations: Decoration[] = [];
			if (start > 0) {
				decorations.push(Decoration.widget(0, document.createElement('div')));
				decorations.push(Decoration.widget(0, document.createElement('div')));
			}
			decorations.push(Decoration.widget(state.doc.content.size, document.createElement('div')));

			if (pluginState.userContentPositions) {
				const [from, to] = pluginState.userContentPositions;
				const decoration = Decoration.inline(
					from,
					to,
					{
						class: 'ai-in-editor-user-content',
						'data-testid': 'ai-in-editor-user-content-decoration',
					},
					{ key: 'inEditorUserContentDecoration' },
				);
				decorations.push(decoration);
			}
			if (pluginState.aiContentPositions) {
				const [from, to] = pluginState.aiContentPositions;
				const decoration = Decoration.inline(
					from,
					to,
					{
						class: 'ai-in-editor-ai-content',
						'data-testid': 'ai-in-editor-ai-content-decoration',
					},
					{ key: 'inEditorAIContentDecoration' },
				);
				decorations.push(decoration);

				const telepointerElement = document.createElement('span');
				telepointerElement.setAttribute('data-testid', `ai-in-editor-telepointer-widget`);
				telepointerElement.className = `ai-in-editor-telepointer`;

				const telepointer = Decoration.widget(to, telepointerElement, {
					side: 0,
					key: 'inEditorAIContentDecoration',
				});

				if (!pluginState.isFinalStream) {
					decorations.push(telepointer);
				}
			}
			return DecorationSet.create(state.doc, decorations);
		},
	});

	const widget = Decoration.widget(start, widgetElement, {
		side: 0,
		key: `aiInEditorWidgetDecoration`,
	});

	return {
		widget,
		offset,
		innerEditorView,
		originalContent,
	};
};

export const getHiddenRangeDecorations = ({
	state,
	initialPositions,
}: {
	state: EditorState;
	initialPositions: [number, number];
}) => {
	/**
	 * We add selection highlight decoration based on the current selection state of the document and the config item in the command palette
	 * If the selection is empty and the document is not empty -> we decorate the entire document unless the config item specifies otherwise
	 */

	const { startPos, endPos } = { startPos: initialPositions[0], endPos: initialPositions[1] };

	const [start, end] = expandPositionsToDocDepth({
		doc: state.doc,
		startPos,
		endPos,
	});

	const decorations: Decoration[] = [];

	state.doc.nodesBetween(start, end, (node, pos) => {
		if (node.isBlock) {
			decorations.push(createHiddenNodeDecoration(pos, pos + node.nodeSize));
		}
		return false;
	});

	return decorations;
};

export const endInnerEditor = createCommand({
	type: 'END',
});
